import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Package, Plus, Check, Truck, ShoppingCart,
  Sparkles, ChevronDown, ChevronUp, Loader2, Receipt
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: "Pendente", color: "bg-neutral-100 text-neutral-700" },
  purchased: { label: "Comprado", color: "bg-blue-100 text-blue-700" },
  delivered: { label: "Entregue", color: "bg-emerald-100 text-emerald-700" },
  used: { label: "Utilizado", color: "bg-purple-100 text-purple-700" }
};

export default function ProjectMaterialsPage() {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [materials, setMaterials] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    quantity: "",
    unit: "un",
    estimated_price: "",
    stage_id: ""
  });

  useEffect(() => {
    fetchData();
  }, [projectId, token]);

  const fetchData = async () => {
    try {
      const [materialsRes, stagesRes] = await Promise.all([
        fetch(`${API_URL}/api/projects/${projectId}/materials`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/projects/${projectId}/stages`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      if (materialsRes.ok) setMaterials(await materialsRes.json());
      if (stagesRes.ok) setStages(await stagesRes.json());
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMaterial = async () => {
    if (!formData.name.trim()) {
      toast.error("Digite o nome do material");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/materials`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          quantity: parseFloat(formData.quantity) || 1,
          unit: formData.unit,
          estimated_price: parseFloat(formData.estimated_price) || 0,
          stage_id: formData.stage_id || null
        })
      });

      if (res.ok) {
        toast.success("Material adicionado!");
        setShowForm(false);
        setFormData({ name: "", quantity: "", unit: "un", estimated_price: "", stage_id: "" });
        fetchData();
      }
    } catch (error) {
      toast.error("Erro ao adicionar material");
    }
  };

  const handleSuggestMaterials = async (stageId) => {
    setSuggesting(true);
    setSelectedStage(stageId);
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/materials/suggest`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ stage_id: stageId })
      });

      if (res.ok) {
        toast.success("Materiais sugeridos adicionados!");
        fetchData();
      }
    } catch (error) {
      toast.error("Erro ao sugerir materiais");
    } finally {
      setSuggesting(false);
      setSelectedStage(null);
    }
  };

  const handleUpdateStatus = async (itemId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/materials/${itemId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success("Status atualizado!");
        fetchData();
      }
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Group materials by stage
  const materialsByStage = stages.map(stage => ({
    ...stage,
    materials: materials.filter(m => m.stage_id === stage.stage_id)
  }));
  const ungroupedMaterials = materials.filter(m => !m.stage_id);

  const totalEstimated = materials.reduce((sum, m) => sum + (m.estimated_price * m.quantity), 0);
  const totalActual = materials.reduce((sum, m) => sum + (m.actual_price * m.quantity), 0);

  return (
    <div className="min-h-screen-mobile bg-white pb-nav" data-testid="project-materials-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-3">
          <button 
            onClick={() => navigate(`/project/${projectId}`)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-neutral-900">Lista de Materiais</h1>
            <p className="text-xs text-neutral-500">{materials.length} itens</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="btn-minimal btn-primary h-9 px-4 text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Adicionar
          </Button>
        </div>
      </header>

      <main className="px-5 py-6 space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 gap-3 animate-up">
          <div className="card-subtle p-4 text-center">
            <p className="text-lg font-semibold text-neutral-900">
              R$ {totalEstimated.toLocaleString("pt-BR")}
            </p>
            <p className="text-xs text-neutral-500 mt-1">Orçado</p>
          </div>
          <div className="card-subtle p-4 text-center">
            <p className="text-lg font-semibold text-emerald-600">
              R$ {totalActual.toLocaleString("pt-BR")}
            </p>
            <p className="text-xs text-neutral-500 mt-1">Gasto</p>
          </div>
        </div>

        {/* Add Form */}
        {showForm && (
          <div className="card-clean p-5 animate-up" data-testid="material-form">
            <h3 className="font-semibold text-neutral-900 mb-4">Novo Material</h3>
            
            <div className="space-y-4">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nome do material"
                className="input-minimal"
                data-testid="material-name-input"
              />

              <div className="grid grid-cols-3 gap-3">
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="Qtd"
                  className="input-minimal"
                />
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                  className="input-minimal"
                >
                  <option value="un">Unidade</option>
                  <option value="m²">m²</option>
                  <option value="m³">m³</option>
                  <option value="kg">kg</option>
                  <option value="l">Litro</option>
                  <option value="saco">Saco</option>
                </select>
                <input
                  type="number"
                  value={formData.estimated_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_price: e.target.value }))}
                  placeholder="R$ Preço"
                  className="input-minimal"
                />
              </div>

              <select
                value={formData.stage_id}
                onChange={(e) => setFormData(prev => ({ ...prev, stage_id: e.target.value }))}
                className="input-minimal"
              >
                <option value="">Etapa (opcional)</option>
                {stages.map(stage => (
                  <option key={stage.stage_id} value={stage.stage_id}>{stage.name}</option>
                ))}
              </select>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowForm(false)}
                  className="btn-minimal btn-secondary flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAddMaterial}
                  className="btn-minimal btn-primary flex-1"
                >
                  Adicionar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Materials by Stage */}
        {materialsByStage.map((stage) => (
          <div key={stage.stage_id} className="animate-up">
            <button
              onClick={() => setExpandedStage(expandedStage === stage.stage_id ? null : stage.stage_id)}
              className="w-full flex items-center justify-between p-4 card-subtle rounded-lg"
            >
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-neutral-500" />
                <div className="text-left">
                  <p className="font-medium text-neutral-900">{stage.name}</p>
                  <p className="text-xs text-neutral-500">{stage.materials.length} materiais</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSuggestMaterials(stage.stage_id);
                  }}
                  disabled={suggesting && selectedStage === stage.stage_id}
                  className="btn-minimal btn-ghost h-8 px-3 text-xs"
                >
                  {suggesting && selectedStage === stage.stage_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3 mr-1" />
                      Sugerir IA
                    </>
                  )}
                </Button>
                {expandedStage === stage.stage_id ? (
                  <ChevronUp className="w-5 h-5 text-neutral-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-neutral-400" />
                )}
              </div>
            </button>

            {expandedStage === stage.stage_id && stage.materials.length > 0 && (
              <div className="mt-2 space-y-2 pl-4">
                {stage.materials.map((material) => (
                  <MaterialItem 
                    key={material.item_id} 
                    material={material} 
                    onUpdateStatus={handleUpdateStatus}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Ungrouped Materials */}
        {ungroupedMaterials.length > 0 && (
          <div className="animate-up">
            <h3 className="font-medium text-neutral-700 mb-3">Sem etapa definida</h3>
            <div className="space-y-2">
              {ungroupedMaterials.map((material) => (
                <MaterialItem 
                  key={material.item_id} 
                  material={material} 
                  onUpdateStatus={handleUpdateStatus}
                />
              ))}
            </div>
          </div>
        )}

        {materials.length === 0 && !showForm && (
          <div className="card-subtle p-8 text-center">
            <Package className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <p className="font-medium text-neutral-900">Nenhum material cadastrado</p>
            <p className="text-sm text-neutral-500 mt-1">
              Adicione materiais ou use a IA para sugerir
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function MaterialItem({ material, onUpdateStatus }) {
  const status = STATUS_CONFIG[material.status] || STATUS_CONFIG.pending;

  return (
    <div className="card-clean p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="font-medium text-neutral-900">{material.name}</p>
          <p className="text-sm text-neutral-500 mt-1">
            {material.quantity} {material.unit}
            {material.estimated_price > 0 && (
              <span> • R$ {(material.estimated_price * material.quantity).toLocaleString("pt-BR")}</span>
            )}
          </p>
          {material.suggested_brand && (
            <p className="text-xs text-neutral-400 mt-1">Marca sugerida: {material.suggested_brand}</p>
          )}
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
          {status.label}
        </span>
      </div>

      {/* Status Actions */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-neutral-100">
        {material.status === "pending" && (
          <button
            onClick={() => onUpdateStatus(material.item_id, "purchased")}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
          >
            <ShoppingCart className="w-3 h-3" />
            Marcar comprado
          </button>
        )}
        {material.status === "purchased" && (
          <button
            onClick={() => onUpdateStatus(material.item_id, "delivered")}
            className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700"
          >
            <Truck className="w-3 h-3" />
            Marcar entregue
          </button>
        )}
        {material.status === "delivered" && (
          <button
            onClick={() => onUpdateStatus(material.item_id, "used")}
            className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700"
          >
            <Check className="w-3 h-3" />
            Marcar utilizado
          </button>
        )}
        {material.invoice_url && (
          <a
            href={material.invoice_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-700"
          >
            <Receipt className="w-3 h-3" />
            Ver NF
          </a>
        )}
      </div>
    </div>
  );
}
